import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { copyToClipboard } from "@/lib/format";
import {
  Target, Crown, Handshake, Lock, LockKeyOpen, ShieldCheck, Envelope, EnvelopeSimpleOpen, Phone,
  LinkedinLogo, Sparkle, ThumbsUp, ThumbsDown, CircleNotch, DownloadSimple, UsersThree,
  Trash, EyeSlash, ArrowsClockwise, Plus, X, ShieldStar, MapPin, Copy, Check, UserPlus, WarningCircle, Buildings,
} from "@phosphor-icons/react";

const CATS = [
  { key: "RECRUITMENT", label: "Recrutement", icon: UserPlus, hint: "Pilote le recrutement — interlocuteur direct pour placer un consultant", scoreKey: "recruitment_relevance_score", scoreLabel: "Pertinence recrutement" },
  { key: "NEED", label: "Besoin", icon: Target, hint: "Pilote l'équipe où le besoin existe", scoreKey: "need_relevance_score", scoreLabel: "Pertinence besoin" },
  { key: "DECISION", label: "Décision", icon: Crown, hint: "Décide ou sponsorise le recours externe", scoreKey: "decision_power_score", scoreLabel: "Pouvoir de décision" },
  { key: "PROCUREMENT", label: "Achat", icon: Handshake, hint: "Référencement ESN & achats IT", scoreKey: "procurement_relevance_score", scoreLabel: "Pertinence achat" },
];

const GEO_TIER = {
  city: { label: "Local", title: "Décideur basé dans la ville du besoin", color: "#00E5FF" },
  region: { label: "Région", title: "Décideur basé dans la région du besoin", color: "#4F46E5" },
  metro: { label: "Aire urbaine", title: "Décideur de la même aire urbaine que le besoin (ex: Paris pour Roissy-en-France)", color: "#7C3AED" },
  hq: { label: "Siège", title: "Aucun contact local trouvé — repli sur le siège / national", color: "#8A8A93" },
};

function scoreColor(s) {
  return s >= 80 ? "#00E5FF" : s >= 60 ? "#4F46E5" : "#8A8A93";
}

function ContactCard({ c, cat, canView, canReveal, remaining, isPaid, isAdmin, signalId, onChanged, onReveal, onUnlock }) {
  const [verdict, setVerdict] = useState(c.my_feedback || null);
  const [busy, setBusy] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);
  const [reported, setReported] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [foundEmail, setFoundEmail] = useState(null);
  const [emailNotFound, setEmailNotFound] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const showFull = canView || c.revealed;
  const emailUnfindable = emailNotFound || c.email_search_status === "NOT_FOUND";
  const sub = c[cat.scoreKey];
  const geo = GEO_TIER[c.location_tier];

  const doReveal = async () => {
    setRevealing(true);
    try { await onReveal(c.id); } finally { setRevealing(false); }
  };

  const copyEmail = async () => {
    const ok = await copyToClipboard(c.email);
    if (ok) {
      setCopied(true);
      toast.success("Email copié");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Impossible de copier l'email");
    }
  };

  const sendFeedback = async (v) => {
    setBusy(true);
    try {
      await api.post(`/opportunities/${signalId}/contacts/${c.id}/feedback`, { verdict: v });
      setVerdict(v);
      toast.success(v === "relevant" ? "Merci — contact marqué pertinent" : "Merci — retour enregistré");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const report = async () => {
    setBusy(true);
    try {
      await api.post(`/opportunities/${signalId}/decision-makers/${c.id}/report`, { reason: "Résultat IA inadapté (signalé par l'ESN)" });
      setReported(true);
      toast.success("Merci — signalement transmis à l'équipe interKO");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const hide = async () => {
    setAdminBusy(true);
    try {
      await api.post(`/admin/opportunities/${signalId}/decision-makers/${c.id}/hide`);
      toast.success("Décideur masqué pour cette opportunité");
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setAdminBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement ce décideur et le blacklister pour cette entreprise ? Il ne réapparaîtra plus, pour aucune ESN.")) return;
    setAdminBusy(true);
    try {
      await api.delete(`/admin/opportunities/${signalId}/decision-makers/${c.id}`);
      toast.success("Décideur supprimé et blacklisté");
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setAdminBusy(false);
    }
  };

  const findEmail = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post(`/opportunities/${signalId}/decision-makers/${c.id}/enrich-email`);
      if (data.status === "success" || data.status === "already") {
        setFoundEmail({ email: data.email, status: data.email_verification_status });
        toast.success(`Email trouvé${data.source ? ` (via ${data.source})` : ""}`);
        onChanged && onChanged();
      } else if (data.status === "providers_unavailable") {
        toast.error("Enrichissement temporairement indisponible. Réessayez bientôt.");
      } else {
        // Échec définitif : on masque le bouton (il ne trouvera rien) et on affiche « Email introuvable ».
        setEmailNotFound(true);
        toast.error("Aucun email trouvé pour ce décideur.");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEmailBusy(false); }
  };

  return (
    <div className="surface rounded-lg p-4 relative" data-testid={`dm-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          {showFull ? (
            <div className="font-display font-bold text-fg text-sm truncate" data-testid={`dm-name-${c.id}`}>{c.name || "—"}</div>
          ) : (
            <div className="flex items-center gap-1.5 text-fg/50 text-sm font-medium" data-testid={`dm-locked-${c.id}`}>
              <Lock size={13} /> Identité masquée
            </div>
          )}
          <div className="text-xs text-muted truncate">{c.title}</div>
          {(c.city || c.region) && (
            <div className="text-[0.7rem] text-muted/80 truncate flex items-center gap-1 mt-0.5" data-testid={`dm-loc-${c.id}`}>
              <MapPin size={10} /> {[c.city, c.region].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
        <span className="shrink-0 font-mono text-xs font-bold px-2 py-1 rounded-full border"
          style={{ color: scoreColor(c.overall_score), borderColor: scoreColor(c.overall_score) + "55", background: scoreColor(c.overall_score) + "14" }}>
          {c.overall_score}
        </span>
      </div>

      {(geo || c.admin_added) && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          {geo && (
            <span className="flex items-center gap-1 text-[0.6rem] font-mono px-1.5 py-0.5 rounded border"
              title={geo.title} data-testid={`dm-geo-${c.id}`}
              style={{ color: geo.color, borderColor: geo.color + "55", background: geo.color + "12" }}>
              <MapPin size={10} weight="fill" /> {geo.label}
            </span>
          )}
          {c.admin_added && (
            <span className="flex items-center gap-1 text-[0.6rem] font-mono px-1.5 py-0.5 rounded border text-accent border-accent/40 bg-accent/10"
              title="Ajouté manuellement par interKO" data-testid={`dm-manual-${c.id}`}>
              <ShieldStar size={10} weight="fill" /> interKO
            </span>
          )}
        </div>
      )}

      <div className="mb-2.5">
        <div className="flex items-center justify-between text-[0.65rem] text-muted mb-1">
          <span>{cat.scoreLabel}</span><span className="font-mono">{sub}/100</span>
        </div>
        <div className="h-1 rounded-full bg-surface-2 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${sub}%`, background: scoreColor(sub) }} />
        </div>
      </div>

      {c.reasoning && <p className="text-[0.72rem] text-fg/70 leading-snug mb-2.5">{c.reasoning}</p>}

      {showFull ? (
        <div className="space-y-1.5 pt-2 border-t border-border">
          {c.email || foundEmail ? (
            <div className="flex items-center gap-1.5" data-testid={`dm-email-row-${c.id}`}>
              <a href={`mailto:${c.email || foundEmail.email}`} className="flex items-center gap-1.5 text-xs text-accent hover:underline min-w-0" data-testid={`dm-email-${c.id}`}>
                <ShieldCheck size={12} weight="fill" className="shrink-0" /> <span className="truncate">{c.email || foundEmail.email}</span>
                {String(c.email_verification_status || foundEmail?.status).toUpperCase() === "VERIFIED" && <span className="text-[0.6rem] bg-accent/15 text-accent px-1 rounded ml-1 shrink-0">vérifié</span>}
                {String(c.email_verification_status || foundEmail?.status).toUpperCase() === "INFERRED" && <span className="text-[0.6rem] bg-amber-500/15 text-amber-400 px-1 rounded ml-1 shrink-0" title="Email déduit du modèle d'emails de l'entreprise — à vérifier" data-testid={`dm-email-inferred-${c.id}`}>déduit</span>}
              </a>
              <button onClick={copyEmail} title="Copier l'email" data-testid={`dm-copy-email-${c.id}`}
                className="shrink-0 p-1 rounded text-muted hover:text-accent hover:bg-accent/10 transition-colors">
                {copied ? <Check size={13} weight="bold" className="text-accent" /> : <Copy size={13} />}
              </button>
            </div>
          ) : emailUnfindable ? (
            <span className="flex items-center gap-1.5 text-xs text-muted" data-testid={`dm-email-unfindable-${c.id}`}>
              <EnvelopeSimpleOpen size={12} /> Email introuvable
            </span>
          ) : isPaid ? (
            <button onClick={findEmail} disabled={emailBusy} data-testid={`dm-find-email-${c.id}`}
              className="flex items-center gap-1.5 text-xs text-accent hover:underline disabled:opacity-50">
              {emailBusy ? <CircleNotch size={12} className="animate-spin" /> : <Envelope size={12} />}
              {emailBusy ? "Recherche de l'email…" : "Trouver l'email"}
            </button>
          ) : <span className="flex items-center gap-1.5 text-xs text-muted"><Envelope size={12} /> Email non disponible</span>}
          <div className="flex items-center gap-3 text-xs text-muted font-mono">
            {c.phone && <span className="flex items-center gap-1"><Phone size={11} /> {c.phone}</span>}
            {c.linkedin && <a href={c.linkedin.startsWith("http") ? c.linkedin : `https://${c.linkedin}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-accent"><LinkedinLogo size={13} /> LinkedIn</a>}
          </div>
          {isPaid && !isAdmin && (
            <div className="flex items-center gap-2 pt-1.5">
              <button disabled={busy || verdict} onClick={() => sendFeedback("relevant")} data-testid={`dm-feedback-good-${c.id}`}
                className={`flex items-center gap-1 text-[0.68rem] px-2 py-1 rounded border ${verdict === "relevant" ? "bg-accent/15 text-accent border-accent/40" : "border-border text-muted hover:text-fg hover:border-accent/30"}`}>
                <ThumbsUp size={11} /> Pertinent
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="pt-2 border-t border-border space-y-2">
          {c.verified_email_available ? (
            <div className="flex items-center gap-1.5 text-[0.68rem] text-accent/80">
              <ShieldCheck size={11} weight="fill" /> Email vérifié disponible
            </div>
          ) : c.email_available === false ? (
            <div className="flex items-center gap-1.5 text-[0.68rem] text-muted" data-testid={`dm-no-email-${c.id}`}>
              <Envelope size={11} /> Email non disponible — révélation du nom sans décompte
            </div>
          ) : null}
          {canReveal ? (
            <button onClick={doReveal} disabled={revealing} data-testid={`dm-reveal-${c.id}`}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold bg-accent/15 text-accent border border-accent/40 rounded-md px-3 py-2 hover:bg-accent/25 disabled:opacity-50">
              {revealing ? <CircleNotch size={13} className="animate-spin" /> : <LockKeyOpen size={13} weight="fill" />}
              {revealing ? "Révélation…"
                : c.email_available === false ? "Voir le nom (gratuit)"
                : `Révéler ce contact${typeof remaining === "number" ? ` · ${remaining} restant${remaining > 1 ? "s" : ""}` : ""}`}
            </button>
          ) : (
            <button onClick={onUnlock} data-testid={`dm-reveal-locked-${c.id}`}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold bg-accent text-black rounded-md px-3 py-2 hover-lift">
              <LockKeyOpen size={13} weight="fill" /> Choisir une offre pour révéler
            </button>
          )}
        </div>
      )}

      {!isAdmin && (
        <div className="pt-2 mt-2 border-t border-border flex items-center justify-end" data-testid={`dm-report-row-${c.id}`}>
          <button disabled={busy || reported} onClick={report} data-testid={`dm-report-${c.id}`}
            className={`flex items-center gap-1 text-[0.68rem] px-2 py-1 rounded border ${reported ? "bg-red-500/15 text-red-400 border-red-400/40" : "border-border text-muted hover:text-red-400 hover:border-red-400/30"}`}>
            <ThumbsDown size={11} /> {reported ? "Erreur signalée" : "Signaler une erreur"}
          </button>
        </div>
      )}

      {isAdmin && (
        <div className="flex items-center gap-2 pt-2 mt-2 border-t border-dashed border-border" data-testid={`dm-admin-actions-${c.id}`}>
          <span className="text-[0.6rem] uppercase tracking-wide text-muted mr-auto flex items-center gap-1"><ShieldStar size={11} weight="fill" className="text-accent" /> Admin</span>
          <button disabled={adminBusy} onClick={hide} data-testid={`dm-hide-${c.id}`}
            className="flex items-center gap-1 text-[0.68rem] px-2 py-1 rounded border border-border text-muted hover:text-amber-400 hover:border-amber-400/40 disabled:opacity-50">
            <EyeSlash size={12} /> Masquer
          </button>
          <button disabled={adminBusy} onClick={remove} data-testid={`dm-delete-${c.id}`}
            className="flex items-center gap-1 text-[0.68rem] px-2 py-1 rounded border border-border text-muted hover:text-red-400 hover:border-red-400/40 disabled:opacity-50">
            <Trash size={12} /> Supprimer + blacklister
          </button>
        </div>
      )}
    </div>
  );
}

function AddDecisionMakerForm({ signalId, onAdded, onCancel }) {
  const [form, setForm] = useState({ name: "", title: "", email: "", linkedin: "", city: "", region: "", category: "NEED" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis."); return; }
    setSaving(true);
    try {
      await api.post(`/admin/opportunities/${signalId}/decision-makers`, form);
      toast.success("Décideur ajouté (visible par toutes les ESN)");
      onAdded && onAdded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <div className="surface rounded-lg p-4 border border-accent/25" data-testid="dm-add-form">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-fg flex items-center gap-2"><Plus size={15} className="text-accent" /> Ajouter un décideur</div>
        <button onClick={onCancel} className="text-muted hover:text-fg" data-testid="dm-add-cancel"><X size={16} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2 mb-2">
        <input value={form.name} onChange={set("name")} placeholder="Nom complet *" data-testid="dm-add-name"
          className="bg-surface-2 border border-border rounded-md px-3 py-2 text-sm text-fg placeholder:text-muted/60" />
        <input value={form.title} onChange={set("title")} placeholder="Poste (ex: Directeur Technique)" data-testid="dm-add-title"
          className="bg-surface-2 border border-border rounded-md px-3 py-2 text-sm text-fg placeholder:text-muted/60" />
        <input value={form.email} onChange={set("email")} placeholder="Email professionnel" data-testid="dm-add-email"
          className="bg-surface-2 border border-border rounded-md px-3 py-2 text-sm text-fg placeholder:text-muted/60" />
        <input value={form.linkedin} onChange={set("linkedin")} placeholder="URL LinkedIn" data-testid="dm-add-linkedin"
          className="bg-surface-2 border border-border rounded-md px-3 py-2 text-sm text-fg placeholder:text-muted/60" />
        <input value={form.city} onChange={set("city")} placeholder="Ville (ex: Lille)" data-testid="dm-add-city"
          className="bg-surface-2 border border-border rounded-md px-3 py-2 text-sm text-fg placeholder:text-muted/60" />
        <input value={form.region} onChange={set("region")} placeholder="Région / Métropole" data-testid="dm-add-region"
          className="bg-surface-2 border border-border rounded-md px-3 py-2 text-sm text-fg placeholder:text-muted/60" />
      </div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-muted">Catégorie :</span>
        {CATS.map((cat) => (
          <button key={cat.key} onClick={() => setForm((f) => ({ ...f, category: cat.key }))} data-testid={`dm-add-cat-${cat.key}`}
            className={`text-xs px-2.5 py-1 rounded border ${form.category === cat.key ? "bg-accent/15 text-accent border-accent/40" : "border-border text-muted hover:text-fg"}`}>
            {cat.label}
          </button>
        ))}
      </div>
      <button onClick={submit} disabled={saving} data-testid="dm-add-submit"
        className="flex items-center gap-2 bg-accent text-black text-sm font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50">
        {saving ? <CircleNotch size={15} className="animate-spin" /> : <Plus size={15} weight="bold" />} Ajouter le décideur
      </button>
    </div>
  );
}

export default function DecisionMakers({ signalId, isPaid, isAdmin, onUnlock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [enrichingAll, setEnrichingAll] = useState(false);
  // Garde-fou anti « spinner infini » : on borne le nombre de sondages de la file de fond.
  const pollsRef = useRef(0);
  const [pollExhausted, setPollExhausted] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/opportunities/${signalId}/decision-makers`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { if (!silent) setLoading(false); }
  }, [signalId]);

  // File asynchrone : tant que l'enrichissement (décideurs OU emails groupés) tourne, on interroge
  // le statut — mais AU PLUS ~72s (18 sondages). Au-delà, on arrête : la recherche est considérée
  // terminée (rien à afficher) plutôt que de faire tourner le spinner indéfiniment.
  const emailBulkRunning = data?.email_enrich_status?.status === "in_progress";
  const MAX_POLLS = 18;
  useEffect(() => {
    const active = data?.enrichment_status === "in_progress" || emailBulkRunning;
    if (!active || pollExhausted) return;
    const t = setInterval(() => {
      pollsRef.current += 1;
      if (pollsRef.current > MAX_POLLS) {
        setPollExhausted(true);
        return;
      }
      load(true);
    }, 4000);
    return () => clearInterval(t);
  }, [data?.enrichment_status, emailBulkRunning, pollExhausted, load]);

  const enrichAllEmails = async () => {
    setEnrichingAll(true);
    try {
      const { data: r } = await api.post(`/opportunities/${signalId}/decision-makers/enrich-all-emails`);
      if (r.status === "done" && !r.total) {
        toast.info("Tous les décideurs ont déjà un email.");
      } else {
        toast.success(`Recherche des emails lancée pour ${r.total} décideur${r.total > 1 ? "s" : ""} — en arrière-plan.`);
        await load(true);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setEnrichingAll(false); }
  };

  useEffect(() => { setData(null); setLoading(false); setShowAdd(false); pollsRef.current = 0; setPollExhausted(false); }, [signalId]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/opportunities/${signalId}/decision-makers/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `interko_decideurs_${signalId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/admin/opportunities/${signalId}/decision-makers/refresh`);
      toast.success("Recherche relancée en arrière-plan — les décideurs apparaîtront ici sous peu.");
      pollsRef.current = 0; setPollExhausted(false);
      await load(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setRefreshing(false); }
  };

  if (!data && !loading) {
    return (
      <div className="surface rounded-lg p-6 sm:p-8 text-center" data-testid="dm-cta">
        <div className="w-12 h-12 rounded-md bg-accent/12 border border-accent/25 flex items-center justify-center mx-auto mb-4">
          <UsersThree size={24} weight="duotone" className="text-accent" />
        </div>
        <h3 className="font-display text-xl font-bold mb-1">Cette opportunité vaut le coup ?</h3>
        <p className="text-sm text-muted max-w-lg mx-auto mb-5">
          Décidez si ce signal mérite d'être poursuivi, puis lancez l'identification des bons interlocuteurs
          (Besoin · Décision · Achat). L'IA cible en priorité des décideurs basés dans la zone du besoin.
        </p>
        <button onClick={load} data-testid="find-decision-makers-btn"
          className="inline-flex items-center gap-2 bg-accent text-black font-semibold px-6 py-3 rounded-md hover-lift">
          <UsersThree size={18} weight="fill" /> Trouver les décideurs
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="surface rounded-lg px-4 py-3" data-testid="dm-loading">
        <div className="bg-accent/8 border border-accent/25 rounded-lg px-4 py-3 flex items-start gap-3">
          <CircleNotch size={18} className="text-accent mt-0.5 shrink-0 animate-spin" />
          <p className="text-sm text-fg/85">
            <span className="font-semibold text-accent">Recherche des décideurs en cours…</span> L'enrichissement s'exécute en arrière-plan (vous n'avez pas à attendre). Les contacts apparaîtront ici automatiquement dès qu'ils sont prêts.
          </p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const canView = data.can_view_identity;
  const canReveal = data.can_reveal_contacts;
  const quota = data.contact_quota || null;
  const remaining = quota ? (quota.unlimited ? null : quota.remaining) : null;
  const exhausted = !!(quota && !quota.unlimited && !canView && quota.remaining <= 0);

  const revealContact = async (contactId) => {
    try {
      const { data: r } = await api.post(`/opportunities/${signalId}/contacts/${contactId}/reveal`);
      const full = r.contact || {};
      setData((prev) => {
        if (!prev) return prev;
        const merge = (arr) => (arr || []).map((c) => (String(c.id) === String(contactId) ? { ...c, ...full, revealed: true, identity_locked: false } : c));
        const next = { ...prev, contact_quota: r.contact_quota || prev.contact_quota };
        for (const k of ["RECRUITMENT", "NEED", "DECISION", "PROCUREMENT", "OTHER"]) next[k] = merge(prev[k]);
        if (prev.hq) { next.hq = { ...prev.hq }; for (const k of ["RECRUITMENT", "NEED", "DECISION", "PROCUREMENT", "OTHER"]) next.hq[k] = merge(prev.hq[k]); }
        return next;
      });
      toast.success(r.charged === false ? "Nom révélé — aucun email disponible, quota non décompté" : "Contact révélé");
    } catch (e) {
      if (e.response?.status === 402) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); onUnlock && onUnlock(); }
      else { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }
  };
  const hqCats = data.hq?.shown ? data.hq : null;
  const isFinalClient = !!data.is_final_client;
  const CAT_BY_KEY = Object.fromEntries(CATS.map((c) => [c.key, c]));
  const orderedCats = (isFinalClient
    ? ["DECISION", "NEED", "PROCUREMENT", "RECRUITMENT"]
    : ["RECRUITMENT", "NEED", "DECISION", "PROCUREMENT"]).map((k) => CAT_BY_KEY[k]);
  const catHint = (cat) => (isFinalClient
    ? { DECISION: "Direction IT / Data / Digital — décide du recours à une ESN",
        NEED: "Responsable opérationnel qui porte le besoin détecté",
        PROCUREMENT: "Achats IT / référencement fournisseurs",
        RECRUITMENT: "Recrutement / RH — complément" }[cat.key] || cat.hint
    : cat.hint);
  const hasLocal = ["RECRUITMENT", "NEED", "DECISION", "PROCUREMENT", "OTHER"].some((k) => (data[k] || []).length);
  const hasHq = hqCats && ["RECRUITMENT", "NEED", "DECISION", "PROCUREMENT", "OTHER"].some((k) => (hqCats[k] || []).length);
  const hasAny = hasLocal || hasHq;
  const pendingEmails = ["RECRUITMENT", "NEED", "DECISION", "PROCUREMENT", "OTHER"]
    .reduce((n, k) => n + (data[k] || []).filter((c) => !(c.email || "").trim()).length
      + (hqCats ? (hqCats[k] || []).filter((c) => !(c.email || "").trim()).length : 0), 0);
  const emailProgress = data.email_enrich_status || null;

  return (
    <div className="space-y-4" data-testid="decision-makers">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="overline flex items-center gap-2"><Sparkle size={13} weight="fill" className="text-accent" /> Décideurs locaux à contacter</div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <button onClick={refresh} disabled={refreshing} data-testid="dm-refresh-btn"
                className="flex items-center gap-1.5 border border-border text-fg text-sm font-medium px-3 py-2 rounded-md hover-lift hover:border-accent/40 disabled:opacity-50">
                <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} /> Relancer la recherche
              </button>
              <button onClick={() => setShowAdd((v) => !v)} data-testid="dm-add-toggle-btn"
                className="flex items-center gap-1.5 border border-border text-fg text-sm font-medium px-3 py-2 rounded-md hover-lift hover:border-accent/40">
                <Plus size={14} /> Ajouter un décideur
              </button>
            </>
          )}
          {canView && pendingEmails > 0 && !emailBulkRunning && (
            <button onClick={enrichAllEmails} disabled={enrichingAll} data-testid="dm-enrich-all-btn"
              className="flex items-center gap-2 border border-accent/40 bg-accent/10 text-accent text-sm font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50">
              <Envelope size={15} weight="fill" /> Trouver tous les emails ({pendingEmails})
            </button>
          )}
          {canView ? (
            <button onClick={exportCsv} data-testid="export-decision-makers-btn"
              className="flex items-center gap-2 border border-border text-fg text-sm font-semibold px-4 py-2 rounded-md hover-lift hover:border-accent/40">
              <DownloadSimple size={15} /> Exporter (CSV)
            </button>
          ) : quota ? (
            exhausted ? (
              <button onClick={onUnlock} data-testid="unlock-decision-makers-btn"
                className="flex items-center gap-2 bg-accent text-black text-sm font-semibold px-4 py-2 rounded-md hover-lift">
                <LockKeyOpen size={15} weight="fill" /> Choisir une offre
              </button>
            ) : (
              <span className="flex items-center gap-2 border border-accent/40 bg-accent/10 text-accent text-sm font-semibold px-4 py-2 rounded-md" data-testid="dm-quota-chip">
                <LockKeyOpen size={15} weight="fill" /> {quota.remaining}/{quota.quota} révélations restantes
              </span>
            )
          ) : null}
        </div>
      </div>

      {emailBulkRunning && (
        <div className="bg-accent/8 border border-accent/25 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="dm-email-enriching-banner">
          <CircleNotch size={18} className="text-accent mt-0.5 shrink-0 animate-spin" />
          <p className="text-sm text-fg/85">
            <span className="font-semibold text-accent">Recherche des emails en cours…</span>{" "}
            {emailProgress ? `${emailProgress.done}/${emailProgress.total} traités · ${emailProgress.found} email(s) trouvé(s).` : ""}
            {" "}Les emails s'affichent automatiquement dès qu'ils sont prêts (vous n'avez pas à attendre).
          </p>
        </div>
      )}

      {canView && data.search_office && (
        <div className="bg-accent/8 border border-accent/25 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="dm-office-banner">
          <MapPin size={18} weight="fill" className="text-accent mt-0.5 shrink-0" />
          <p className="text-sm text-fg/85">
            Décideurs recherchés pour l'implantation{" "}
            <span className="font-semibold text-accent" data-testid="dm-office-city">{data.search_office.city || data.search_office.name}</span>
            {data.search_office.region ? ` (${data.search_office.region})` : ""}
            {" · "}
            <span className="text-xs font-mono text-muted">{data.search_office.is_headquarters ? "Siège" : "Agence locale"}</span>
            {" — la géographie du besoin prime sur celle du siège."}
          </p>
        </div>
      )}

      {data.enrichment_status === "in_progress" && !pollExhausted && (
        <div className="bg-accent/8 border border-accent/25 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="dm-enriching-banner">
          <CircleNotch size={18} className="text-accent mt-0.5 shrink-0 animate-spin" />
          <p className="text-sm text-fg/85">
            <span className="font-semibold text-accent">Recherche des décideurs en cours…</span> L'enrichissement s'exécute en arrière-plan (vous n'avez pas à attendre). Les contacts apparaîtront ici automatiquement dès qu'ils sont prêts.
          </p>
        </div>
      )}

      {data.usage?.quota_exhausted && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="dm-quota-banner">
          <WarningCircle size={18} className="text-amber-400 mt-0.5 shrink-0" weight="fill" />
          <p className="text-sm text-fg/85">
            <span className="font-semibold text-amber-400">Quota quotidien Prospeo épuisé</span> — la limite de <b>1000 requêtes de recherche par jour</b> est atteinte (indépendant de vos crédits, qui ne sont pas consommés). La recherche de décideurs reprendra automatiquement après la réinitialisation quotidienne. Réessayez plus tard.
          </p>
        </div>
      )}
      {data.usage?.rate_limited && !data.usage?.quota_exhausted && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="dm-rate-banner">
          <WarningCircle size={18} className="text-amber-400 mt-0.5 shrink-0" weight="fill" />
          <p className="text-sm text-fg/85">
            <span className="font-semibold text-amber-400">Débit Prospeo momentanément saturé</span> (1 req/s · 30/min). Patientez une minute puis relancez la recherche.
          </p>
        </div>
      )}

      {isAdmin && showAdd && (
        <AddDecisionMakerForm signalId={signalId} onCancel={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); load(); }} />
      )}

      {!canView && quota && (
        exhausted ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="dm-quota-exhausted-banner">
            <Lock size={18} className="text-amber-400 mt-0.5 shrink-0" weight="fill" />
            <p className="text-sm text-fg/85">
              <span className="font-semibold text-amber-400">Vous avez utilisé vos {quota.quota} contacts{quota.window === "month" ? " du mois" : " d'essai"}.</span>{" "}
              Choisissez une offre pour continuer à accéder aux coordonnées des décideurs.{" "}
              <button onClick={onUnlock} className="text-accent font-semibold hover:underline" data-testid="dm-quota-upgrade-link">Voir les offres →</button>
            </p>
          </div>
        ) : (
          <div className="bg-accent/8 border border-accent/25 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="dm-paywall-banner">
            <LockKeyOpen size={18} className="text-accent mt-0.5 shrink-0" weight="fill" />
            <p className="text-sm text-fg/85">
              L'IA a identifié les bons interlocuteurs. Cliquez <span className="text-accent font-medium">« Révéler ce contact »</span> pour débloquer l'identité, l'email vérifié et les coordonnées.{" "}
              Il vous reste <span className="text-accent font-semibold">{quota.remaining}</span> révélation{quota.remaining > 1 ? "s" : ""} sur {quota.quota}{quota.window === "month" ? " ce mois-ci" : (data.effective_plan === "mission_express" ? "" : " pour votre essai")}.
            </p>
          </div>
        )
      )}

      {isFinalClient && hasLocal && (
        <div className="overline flex items-center gap-2 mb-1" data-testid="dm-local-header">
          <MapPin size={14} weight="duotone" className="text-accent" /> Décideurs locaux à contacter
        </div>
      )}
      {orderedCats.map((cat) => {
        const items = data[cat.key] || [];
        if (!items.length) return null;
        const Icon = cat.icon;
        return (
          <div key={cat.key} data-testid={`dm-cat-${cat.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={16} weight="duotone" className="text-accent" />
              <span className="text-sm font-semibold text-fg">{cat.label}</span>
              <span className="text-xs text-muted">· {catHint(cat)}</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((c) => (
                <ContactCard key={c.id} c={c} cat={cat} canView={canView} canReveal={canReveal} remaining={remaining} isPaid={isPaid} isAdmin={isAdmin} signalId={signalId} onChanged={() => load(true)} onReveal={revealContact} onUnlock={onUnlock} />
              ))}
            </div>
          </div>
        );
      })}

      {data.hq?.shown && (
        <div className="pt-4 mt-2 border-t border-dashed border-border" data-testid="dm-hq-section">
          <div className="overline flex items-center gap-2 mb-1"><Buildings size={14} weight="duotone" className="text-[#8A8A93]" /> Décideurs siège / HQ à contacter</div>
          <p className="text-xs text-muted mb-3">
            {data.hq.reason === "local_insufficient"
              ? "Aucun décideur local suffisant — repli automatique sur le siège / national."
              : "Décideurs pertinents au niveau du siège / national."}
          </p>
          {canView && data.hq.office && (
            <div className="bg-surface-2/50 border border-border rounded-lg px-4 py-2.5 flex items-start gap-3 mb-3" data-testid="dm-hq-office-banner">
              <Buildings size={16} weight="fill" className="text-[#8A8A93] mt-0.5 shrink-0" />
              <p className="text-sm text-fg/80">
                Siège :{" "}
                <span className="font-semibold text-fg" data-testid="dm-hq-office-city">{data.hq.office.city || data.hq.office.name}</span>
                {data.hq.office.region ? ` (${data.hq.office.region})` : ""}
              </p>
            </div>
          )}
          {orderedCats.map((cat) => {
            const items = (data.hq[cat.key] || []);
            if (!items.length) return null;
            const Icon = cat.icon;
            return (
              <div key={`hq-${cat.key}`} data-testid={`dm-cat-hq-${cat.key}`} className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={16} weight="duotone" className="text-[#8A8A93]" />
                  <span className="text-sm font-semibold text-fg">{cat.label}</span>
                  <span className="text-[0.6rem] font-mono px-1.5 py-0.5 rounded border text-[#8A8A93] border-[#8A8A93]/40 bg-[#8A8A93]/10">HQ</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((c) => (
                    <ContactCard key={c.id} c={c} cat={cat} canView={canView} canReveal={canReveal} remaining={remaining} isPaid={isPaid} isAdmin={isAdmin} signalId={signalId} onChanged={() => load(true)} onReveal={revealContact} onUnlock={onUnlock} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!hasAny && (data.enrichment_status !== "in_progress" || pollExhausted) && (
        <div className="surface rounded-lg p-8 text-center" data-testid="dm-empty">
          {isAdmin ? (
            <>
              <p className="text-sm text-fg/80">Aucun décideur local identifié pour cette entreprise via nos sources pour le moment.</p>
              <p className="text-xs text-muted mt-1">Nous ne proposons que des décideurs de la ville/région du besoin. Utilisez «&nbsp;Relancer la recherche&nbsp;» ou ajoutez un décideur manuellement.</p>
            </>
          ) : (
            <p className="text-sm text-fg/80 max-w-xl mx-auto leading-relaxed">
              Aucun décideur pertinent n'a encore été identifié pour cette entreprise à partir de nos sources actuelles. Vous pouvez nous contacter à{" "}
              <a href="mailto:support@interko.io" className="text-accent hover:underline" data-testid="dm-support-link">support@interko.io</a>{" "}
              afin que nous effectuions une recherche approfondie et ciblée des interlocuteurs les plus pertinents.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
